import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Step from '../../../../../../../features/components/Forms/StepForm/Step'
import RadioBlock from '../../../../../../../components/Form/RadioBlock'
import Spoiler from '../../../../../../../components/Spoiler'
import GeographySelect from './GeographySelect'
import DeviceCategorySelect from './DeviceCategorySelect'
import BrowserLanguageSelect from './BrowserLanguageSelect'
import DeviceManufacturerSelect from './DeviceManufacturerSelect'
import OperatingSystemSelect from './OperatingSystemSelect'
import ContentTargetingSelect from './ContentTargetingSelect'
// import AudienceSegmentsSelect from './AudienceSegmentsSelect'
import CustomTargetingKeyValuesSelectors from './CustomTargetingKeyValuesSelectors'
import FieldLabelWrapper from '../../../../../../../features/components/Form/FieldLabelWrapper'
import InventoryOverviewSpoiler from '../../../components/InventoryOverviewSpoiler'

import { ATTACH_TARGETING_OPTIONS } from '../../fields'
import { OPTION_NO, OPTION_YES } from '../../../../../../../constants/forms'

import { darkGrey } from '../../../../../../../styles/const/colors'
import { rightSelector } from '../../../../../../../modules/selectors/rights'
import {
  CUSTOM_TARGETING_ENABLED,
  DEVICE_TARGETING_ENABLED,
  GEO_TARGETING_ENABLED
} from '../../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/fields'

const stepFields = [ATTACH_TARGETING_OPTIONS]

const TargetingOptionsStep = ({ formik, handleStepChange }) => {
  const { t } = useTranslation()

  const right = useSelector(rightSelector)
  const rightAdvancedSettings = right?.advanced_settings || {}
  const {
    [GEO_TARGETING_ENABLED]: geoTargetingEnabled,
    [DEVICE_TARGETING_ENABLED]: deviceTargetingEnabled,
    [CUSTOM_TARGETING_ENABLED]: customTargetingEnabled
  } = rightAdvancedSettings

  const { values, setFieldValue, errors, touched } = formik

  const { stepTitle, spoilersInitialOpen } = useMemo(() => {
    let stepTitle = 'Do you want to attach any targeting options to this line item?'
    let spoilersInitialOpen = false

    if (!geoTargetingEnabled) {
      stepTitle = 'Do you want to apply other targeting settings to this line item?'
      spoilersInitialOpen = true
    }

    if (geoTargetingEnabled && (deviceTargetingEnabled || customTargetingEnabled)) {
      stepTitle = 'Do you want to geo-target this line item or apply other targeting settings?'
      spoilersInitialOpen = false
    }

    if (geoTargetingEnabled && !deviceTargetingEnabled && !customTargetingEnabled) {
      stepTitle = 'Do you want to geo-target this line item?'
      spoilersInitialOpen = false
    }

    return {
      stepTitle,
      spoilersInitialOpen
    }
  }, [geoTargetingEnabled, deviceTargetingEnabled, customTargetingEnabled])

  const handleContinue = useCallback(() => {
    handleStepChange(stepFields)
  }, [handleStepChange])

  return (
    <Step stepTitle={stepTitle} handleContinue={handleContinue}>
      <InventoryOverviewSpoiler />
      <RadioBlock
        setFieldValue={setFieldValue}
        id={'radio_targeting_options' + OPTION_YES}
        name={ATTACH_TARGETING_OPTIONS}
        value={OPTION_YES}
        selectedValue={values[ATTACH_TARGETING_OPTIONS]}
        label="Yes"
        error={errors[ATTACH_TARGETING_OPTIONS]}
        touched={touched[ATTACH_TARGETING_OPTIONS]}
      >
        <p>{t('Select specific country or countries to target:')}</p>
        {geoTargetingEnabled && (
          <FieldLabelWrapper label={t('Geo-targeting')}>
            <GeographySelect formik={formik} />
          </FieldLabelWrapper>
        )}

        {deviceTargetingEnabled && (
          <Spoiler
            hasMarginLeft={false}
            isSmall={true}
            initialOpen={spoilersInitialOpen}
            title="Default targeting options"
            titleColor={darkGrey}
          >
            <p>{t('Select specific devices or browsers to target:')}</p>
            <DeviceCategorySelect formik={formik} />
            <BrowserLanguageSelect formik={formik} />
            <DeviceManufacturerSelect formik={formik} />
            <OperatingSystemSelect formik={formik} />
          </Spoiler>
        )}

        {customTargetingEnabled && (
          <Spoiler
            hasMarginLeft={false}
            isSmall={true}
            initialOpen={spoilersInitialOpen}
            title="Custom targeting options"
            titleColor={darkGrey}
          >
            <p>{t('Select specific key-values, content or metadata to target:')}</p>
            {/*AudienceSegmentsSelect is commented out until we have a GAM account with access to Audience solutions*/}
            {/*todo PBR-1311 Uncomment when Audience Segments are available*/}
            {/*<AudienceSegmentsSelect formik={formik} />*/}
            <CustomTargetingKeyValuesSelectors formik={formik} />
            <ContentTargetingSelect formik={formik} />
          </Spoiler>
        )}
      </RadioBlock>
      <RadioBlock
        setFieldValue={setFieldValue}
        id={'radio_targeting_options' + OPTION_NO}
        name={ATTACH_TARGETING_OPTIONS}
        value={OPTION_NO}
        selectedValue={values[ATTACH_TARGETING_OPTIONS]}
        label="No"
      />
    </Step>
  )
}

export default TargetingOptionsStep
